import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, CheckMarkIcon } from '@blade/ui-kit'
import { basicModeAtom } from '@root/entities/basic'
import { AnnouncementShowToggle } from '@root/shared/ui/announcement'
import { AnnouncementHeader } from '@root/shared/ui/announcement/announcement-header'
import { Dialog, Separator as Separator_ } from '@root/shared/ui-kit'

import {
  browserLocalWarningDialogOpenAtom,
  browserLocalWarningSeenAtom,
} from '../atoms'

type Props = {}

export const BrowserLocalWarningDialog: React.FC<Props> = () => {
  const { t } = useTranslation()
  const [skipWarning, setSkipWarning] = useAtom(browserLocalWarningSeenAtom)
  const [open, setOpen] = useAtom(browserLocalWarningDialogOpenAtom)
  // A warning about extensions reaching into wallet transactions. Basic has
  // no wallet in the browser — its trades are signed on the server — so it
  // is shown only once the mode is known to be the one with wallets.
  const [tradingMode] = useAtom(basicModeAtom)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setOpen(false)
  }

  const toggleSkipWarning = (checked: boolean | 'indeterminate') =>
    setSkipWarning(typeof checked === 'boolean' ? checked : skipWarning)

  return (
    <Root
      open={open && tradingMode === 'advanced'}
      describedBy={'browser-local-warning-dialog'}
    >
      <Content>
        <AnnouncementHeader
          title={t('browser-mode.warning.title')}
          bigTitle={t('browser-mode.warning.big-title')}
          onClose={() => setOpen(false)}
        />
        <Body>
          <Heading>{t('browser-mode.warning.heading')}</Heading>
          <Description>{t('browser-mode.warning.description')}</Description>
          <Separator />
        </Body>
        <Footer>
          <ButtonAlt
            variant={'solid'}
            color={'primary'}
            fullWidth
            radius={'rounded'}
            onClick={handleClick}
          >
            <CheckMarkIcon size={14} />
            <ButtonText>{t('browser-mode.warning.button')}</ButtonText>
          </ButtonAlt>
          <AnnouncementShowToggle
            checked={skipWarning}
            onChange={(checked) => toggleSkipWarning(checked)}
          />
        </Footer>
      </Content>
    </Root>
  )
}

const Root = styled(Dialog)`
  background: var(--background-2);

  width: 400px;
  display: grid;
`

const Content = styled.div`
  overflow: hidden;
`

const Body = styled.div`
  padding: 20px 22px 4px;

  font-size: 14px;
  color: var(--text-10);
`

const Footer = styled.div`
  padding: 0 20px 20px;
  gap: 20px;

  display: grid;
  gap: 10px;
  place-items: center;
`

const Heading = styled.h2`
  color: var(--text-12);

  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  margin-bottom: 4px;
`

const Description = styled.p`
  color: var(--text-9);

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`

const Separator = styled(Separator_)`
  margin: 16px 0;
  opacity: 0.4;
`

const ButtonAlt = styled(Button)`
  color: var(--textA-11);
  gap: 0;

  > svg {
    margin-inline-end: 6px;
    margin-inline-start: 4px;
  }
`

const ButtonText = styled.span`
  color: var(--text-12);

  font-weight: 700;
  height: 18px;
  margin-inline-end: 12px;
  padding-inline-end: 12px;

  display: grid;
  place-content: center;
`
